import React, { useState } from 'react';
import { dictionaryTerms } from '../mockData/mock';
import { BookOpen, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

const Dictionary = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTerms = dictionaryTerms.filter(term =>
    term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
    term.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group terms alphabetically
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const firstLetter = term.term[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(term);
    return acc;
  }, {});

  const letters = Object.keys(groupedTerms).sort();

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#f7f5f2] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#a8b5a0]/10 px-4 py-2 rounded-full mb-6">
            <BookOpen className="h-5 w-5 text-[#a8b5a0]" />
            <span className="text-sm font-medium text-[#736c64]">Learn the Language</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-6">
            Sourdough Dictionary
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto">
            Understand the terminology and science behind sourdough baking. 
            No jargon, just clear explanations.
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-white border-b border-[#e8e6e1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#736c64]" />
            <Input
              type="text"
              placeholder="Search terms or definitions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
            />
          </div>
        </div>
      </section>

      {/* Dictionary Terms */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {letters.length > 0 ? (
            <div className="space-y-12">
              {letters.map(letter => (
                <div key={letter}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-[#a8b5a0] rounded-lg flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{letter}</span>
                    </div>
                    <div className="flex-grow h-px bg-[#e8e6e1]"></div>
                  </div>

                  <div className="space-y-4">
                    {groupedTerms[letter].map((term, index) => (
                      <Card key={index} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <h3 className="text-xl font-semibold text-[#1e1919] mb-3">
                            {term.term}
                          </h3>
                          <p className="text-[#736c64] leading-relaxed">
                            {term.definition}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-[#e8e6e1] mx-auto mb-4" />
              <p className="text-lg text-[#736c64]">
                No terms found matching "{searchTerm}"
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1e1919] mb-4">
            Have a Question About a Term?
          </h2>
          <p className="text-lg text-[#736c64] mb-8">
            Check out our FAQ or get in touch with us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/faq"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#a8b5a0] hover:bg-[#8fa087] text-white rounded-lg font-medium transition-colors"
            >
              View FAQ
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#e8e6e1] hover:bg-white text-[#736c64] rounded-lg font-medium transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dictionary;
